import React, { useState } from 'react';
import { GraduationCap, MapPin, Send, Phone, Mail, User, Briefcase } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SFXAcademySection = ({ t }) => {
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    occupation: '',
    country: '',
    phone: '',
    email: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/academy/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          phone_prefix: ''
        })
      });
      if (res.ok) {
        toast.success(t.academy.form.success);
        setFormData({ full_name: '', occupation: '', country: '', phone: '', email: '' });
        setShowForm(false);
      } else {
        toast.error('Error al enviar. Intenta de nuevo.');
      }
    } catch {
      toast.error('Error de conexión. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="protocols" className="py-24 relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://customer-assets.emergentagent.com/job_67b366a2-9c46-4ca8-9e2f-e7e30db89ded/artifacts/dna6twls_imagen%20fondo%20de%20SFX%20Academy.png"
          alt="SFX Academy Workshop"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-white/85"></div>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with Logo */}
        <div className="text-center mb-12">
          <img
            src="https://customer-assets.emergentagent.com/job_67b366a2-9c46-4ca8-9e2f-e7e30db89ded/artifacts/pzxgkn2w_SFX%20ACADEMY.png"
            alt="SFX Academy - Training Expert"
            className="h-28 w-auto mx-auto mb-8"
            data-testid="sfx-academy-logo"
          />
          <p className="text-lg text-slate-700 max-w-3xl mx-auto leading-relaxed mb-4">
            {t.academy.description}
          </p>
          <p className="text-base text-slate-600 max-w-3xl mx-auto leading-relaxed mb-6">
            {t.academy.detail}
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-slate-500 max-w-3xl mx-auto mb-8">
            <MapPin className="w-4 h-4 flex-shrink-0" />
            <p className="leading-relaxed">{t.academy.countries}</p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => setShowForm(true)}
              size="lg"
              data-testid="academy-register-btn"
              className="bg-slate-800 hover:bg-slate-900 text-white px-10 py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
            >
              <GraduationCap className="mr-2 h-5 w-5" />
              {t.academy.cta}
            </Button>
            <a
              href="https://wa.me/13059795251"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="academy-whatsapp-btn"
            >
              <Button
                variant="outline"
                size="lg"
                className="border-slate-700 text-slate-700 hover:bg-slate-800 hover:text-white px-10 py-6 text-lg font-semibold rounded-lg transition-all duration-300"
              >
                <Phone className="mr-2 h-5 w-5" />
                {t.academy.whatsappCta}
              </Button>
            </a>
          </div>
        </div>

        {/* Registration Form */}
        {showForm && (
          <div className="max-w-2xl mx-auto mt-12">
            <Card className="border-2 border-slate-200 shadow-xl">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center">{t.academy.form.title}</h3>
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Full Name */}
                  <div className="space-y-2">
                    <Label className="text-base font-semibold flex items-center text-slate-700">
                      <User className="w-4 h-4 mr-2" />
                      {t.academy.form.fullName}
                    </Label>
                    <Input
                      value={formData.full_name}
                      onChange={(e) => handleChange('full_name', e.target.value)}
                      required
                      data-testid="academy-fullname"
                      className="h-12 border-slate-300 focus:border-slate-800 focus:ring-slate-800"
                    />
                  </div>

                  {/* Occupation */}
                  <div className="space-y-2">
                    <Label className="text-base font-semibold flex items-center text-slate-700">
                      <Briefcase className="w-4 h-4 mr-2" />
                      {t.academy.form.occupation}
                    </Label>
                    <Select value={formData.occupation} onValueChange={(v) => handleChange('occupation', v)} required>
                      <SelectTrigger data-testid="academy-occupation" className="h-12 border-slate-300">
                        <SelectValue placeholder={t.academy.form.occupation} />
                      </SelectTrigger>
                      <SelectContent>
                        {t.academy.form.occupationOptions.map((opt) => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Country */}
                  <div className="space-y-2">
                    <Label className="text-base font-semibold flex items-center text-slate-700">
                      <MapPin className="w-4 h-4 mr-2" />
                      {t.academy.form.country}
                    </Label>
                    <Input
                      value={formData.country}
                      onChange={(e) => handleChange('country', e.target.value)}
                      required
                      data-testid="academy-country"
                      className="h-12 border-slate-300 focus:border-slate-800 focus:ring-slate-800"
                    />
                  </div>

                  {/* Phone */}
                  <div className="space-y-2">
                    <Label className="text-base font-semibold flex items-center text-slate-700">
                      <Phone className="w-4 h-4 mr-2" />
                      {t.academy.form.phone}
                    </Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      required
                      type="tel"
                      data-testid="academy-phone"
                      className="h-12 border-slate-300 focus:border-slate-800 focus:ring-slate-800"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label className="text-base font-semibold flex items-center text-slate-700">
                      <Mail className="w-4 h-4 mr-2" />
                      {t.academy.form.email}
                    </Label>
                    <Input
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      required
                      type="email"
                      data-testid="academy-email"
                      className="h-12 border-slate-300 focus:border-slate-800 focus:ring-slate-800"
                    />
                  </div>

                  {/* Submit */}
                  <Button
                    type="submit"
                    size="lg"
                    disabled={loading}
                    data-testid="academy-submit-btn"
                    className="w-full bg-slate-800 hover:bg-slate-900 text-white h-14 text-lg font-semibold rounded-lg shadow-lg transition-all duration-300"
                  >
                    <Send className="mr-2 h-5 w-5" />
                    {loading ? '...' : t.academy.form.submit}
                  </Button>
                </form>

                {/* WhatsApp info link */}
                <div className="mt-6 text-center">
                  <a
                    href="https://wa.me/13059795251"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-slate-500 hover:text-slate-800 transition-colors"
                  >
                    {t.academy.whatsappCta}: +1 (305) 979-5251
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </section>
  );
};
